<template>
  <div class="base-info">
    <div class="base-info-container">
      <div class="base-info-top">
        <div class="avatar-container" :class="[candidateInfo.is_employee && 'employee', candidateInfo.is_alumni && 'alumni']">
          <div class="avatar-wrapper" :class="candidateInfo.is_agency_uploaded_and_hid && 'is-lock'" @click="handlePreviewAvatar">
            <img
              class="avatar"
              :src="avatarPath"
              @error="handleAvatarError"
            />
            <i v-if="candidateInfo.is_agency_uploaded_and_hid" class="avatar-lock-icon iconfont iconLock"></i>
          </div>
          <div v-show="isPreviewAvatar" class="avatar-preview-mask">
            <div class="preview-wrapper">
              <img class="avatar-preview" :src="originalAvatarPath" @error="handleAvatarError"/>
              <i class="close-preview-icon iconfont iconzengjiayulan-04" @click="handleClosePreviewAvatar"></i>
            </div>
          </div>
          <!-- application is always been viewed at profile page, so hide this red point -->
          <div v-if="false" class="badge"></div>
        </div>
        <div class="info-container">
          <div class="name-container">
            <span class="name">{{ candidateName }}</span>
            <i v-if="candidateInfo.gender" class="gender-icon" :class="genderIconClass"></i>
            <DuplicateCandidatePopover
              v-if="!isAgency && hasDuplicateCandidatePermission && !candidateInfo.is_agency_uploaded_and_hid && duplicateCandidateList && duplicateCandidateList.length > 0"
              :duplicateCandidateList="duplicateCandidateList"
              @openDuplicateCandidateDetail="openDuplicateCandidateDetail"
            >
              <DuplicateCandidateIcon :size="20"></DuplicateCandidateIcon>
            </DuplicateCandidatePopover>
            <div v-auto-tooltip.always="getCopiedFromJob(candidateInfo)" v-if="candidateInfo.copied_info" class="is-copy-application">
              <i class="iconfont icona-bianzu53"></i>
              <i class="iconfont icona-copybuttonforcoach1 dot"></i>
            </div>
            <el-tooltip v-if="candidateInfo.cv_owner" effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.message.cvOwner`, {name: candidateInfo.cv_owner.name})">
              <div class="cv-owner">{{ candidateInfo.cv_owner.name }}</div>
            </el-tooltip>
          </div>
          <div class="info-card">
            <div class="info">
              <template v-for="(item, index) in infoList">
                <div v-if="item.label && !item.hidden" class="info-item" :key="index" :class="item.class" :style="item.style">
                  <span class="label">{{ item.label }}</span>
                  <span :title="item.value" class="content">{{ item.value|formatValue }}</span>
                </div>
                <a
                  v-else-if="item.isEmail && item.value && !item.hidden"
                  class="info-item active"
                  :style="item.style"
                  :class="item.class"
                  :href="`mailto:${item.value}`"
                  :key="index"
                >
                  {{ item.value|formatValue }}
                </a>
                <div v-else-if="!item.hidden" class="info-item" :key="index" :class="item.class" :style="item.style">{{ item.value|formatValue }}</div>
              </template>
            </div>
            <div v-if="isTalentPool" class="description" style="flex: 1">{{ candidateInfo.description }}</div>
          </div>
        </div>
      </div>
      <div v-if="!isTalentPool" class="description">{{ candidateInfo.description }}</div>
    </div>
    <div v-if="!hiddenCandidateStatus" class="divider"></div>
    <CandidateStatus
      v-if="!hiddenCandidateStatus"
      :currentStatus="currentStatus"
      :statusGroupList="statusGroupList"
      :disableChangeButton="!canChangeStatus"
      :tooltip="tooltip"
      @toRejectStatus="handleToRejectStatus"
      @changeStatus="handleChangeStatus"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { getI18nPropertyValue } from '@/i18n'
import DuplicateCandidateIcon from '@/components/icon/duplicateCandidate.vue'
import CandidateStatus from './candidateStatus.vue'
import DuplicateCandidatePopover from '../../../application/components/duplicateCandidatePopover.vue'

export default {
  components: {
    CandidateStatus,
    DuplicateCandidateIcon,
    DuplicateCandidatePopover
  },
  props: {
    candidateInfo: {
      required: true
    },
    statusGroupList: {
      type: Array,
      required: true
    },
    hiddenCandidateStatus: {
      type: Boolean,
      default: false
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      getI18nPropertyValue,
      missedAvatar: false,
      isPreviewAvatar: false,
      duplicateCandidateList: []
    }
  },
  computed: {
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    },
    avatarPath () {
      let path = this.candidateInfo.photo
      if (this.missedAvatar) {
        path = this.$DjangoBaseUrl(this.candidateInfo.default_photo)
      } else if (this.is_agency_uploaded_and_hid) {
        path = this.$DjangoStaticUrl('images_en/default.jpg')
      }
      return path
    },
    originalAvatarPath () {
      if (this.missedAvatar) {
        return this.$DjangoBaseUrl(this.candidateInfo.default_photo)
      } else {
        return this.$DjangoBaseUrl(this.candidateInfo.original_photo)
      }
    },
    currentStatus () {
      return this.candidateInfo.current_status
    },
    canChangeStatus () {
      return !this.candidateInfo.is_agency_uploaded_and_hid && !this.isDisabled
    },
    tooltip () {
      return this.isDisabled ? this.$t(`${this.i18nPrefix}.message.disabledTip`) : ''
    },
    infoList () {
      const candidateInfo = this.candidateInfo
      const notHide = !candidateInfo.is_agency_uploaded_and_hid
      const infoWidth = 'width: 50%'
      const list = [
        {
          isEmail: notHide,
          class: 'email',
          style: this.isTalentPool || this.isPotential ? infoWidth : '',
          value: notHide ? candidateInfo.email : '***'
        },
        {
          label: `${this.$t(`${this.i18nPrefix}.label.age`)}: `,
          class: 'age',
          style: this.isTalentPool || this.isPotential ? infoWidth : '',
          value: this.age
        },
        {
          label: `${this.$t(`${this.i18nPrefix}.label.appliedDate`)}: `,
          class: 'applied-date',
          hidden: this.isTalentPool || this.isPotential,
          value: moment(candidateInfo.applied_job_date).format('YYYY/MM/DD')
        },
        {
          class: 'phone',
          style: this.isTalentPool || this.isPotential ? infoWidth : '',
          value: notHide ? candidateInfo.mobileWithCountryCode : '***'
        },
        {
          class: 'location',
          style: this.isTalentPool || this.isPotential ? infoWidth : '',
          value: this.getI18nPropertyValue(this.candidateInfo.location.province, 'cn_name', 'name')
        },
        {
          label: `${this.$t(`${this.i18nPrefix}.label.appliedJob`)}: `,
          class: 'applied-job',
          hidden: this.isTalentPool || this.isPotential,
          value: this.candidateInfo.job?.i18nName
        }
      ]
      return list
    },
    age () {
      if (!this.candidateInfo.birthday) {
        return
      }
      return this.candidateInfo.yearsOld
    },
    genderIconClass () {
      if (this.candidateInfo.gender === 'F') {
        return 'iconfont iconResume-female'
      }
      if (this.candidateInfo.gender === 'M') {
        return 'iconfont iconResume-male'
      }
      return ''
    },
    candidateName () {
      const candidateInfo = this.candidateInfo
      let name = candidateInfo.other_name
      if (candidateInfo.is_agency_uploaded_and_hid) {
        name = this.$t('admin.application.applicantList.label.hidedCandidateName')
      }
      return name
    }
  },
  created () {
    this.loadDuplicateCandidate()
  },
  methods: {
    ...mapActions('application', ['getApplicationDuplicateCandidateInfo']),
    async loadDuplicateCandidate () {
      if (!this.isTalentPool && !this.isPotential && !this.isAgency) {
        const type = this.$route.query.page_from
        const params = {
          job_id: this.candidateInfo.job.id,
          candidate_id: this.candidateInfo.id,
          type
        }
        const resp = await this.getApplicationDuplicateCandidateInfo(params)
        if (resp) {
          this.duplicateCandidateList = resp
        }
      }
    },
    handleAvatarError () {
      this.missedAvatar = true
    },
    handleToRejectStatus () {
      this.$emit('toRejectStatus')
    },
    handleChangeStatus (status) {
      this.$emit('changeStatus', status)
    },
    handlePreviewAvatar () {
      if (this.candidateInfo.is_agency_uploaded_and_hid) {
        return
      }
      this.isPreviewAvatar = true
    },
    handleClosePreviewAvatar () {
      this.isPreviewAvatar = false
    },
    openDuplicateCandidateDetail (application) {
      const { candidateId, jobId } = application
      const backUrl = this.$route.query.back
      window.open(`/v-admin/application/profile?candidate_id=${candidateId}&job_id=${jobId}&back=${backUrl}`)
    },
    getCopiedFromJob (candidateInfo) {
      const copiedInfo = candidateInfo.copied_info
      const isCopiedFromJob = copiedInfo.is_copied_from_job
      if (isCopiedFromJob) {
        return `${this.$t('admin.application.candidate.label.copiedFrom')}${getI18nPropertyValue(copiedInfo.copied_from_job, 'cn_title', 'title')} - ${copiedInfo.copied_from_job.id}`
      }
      return `${this.$t('admin.application.candidate.label.copiedFrom')}${this.$t('admin.talent.pool.title')}`
    }
  },
  watch: {
    avatarPath: {
      immediate: true,
      handler () {
        if (!this.missedAvatar && !this.candidateInfo.photo) {
          this.missedAvatar = true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.base-info {
  display: flex;

  .base-info-container {
    flex: 1;
    overflow: hidden;

    .base-info-top {
      display: flex;
      align-items: center;

      .avatar-container {
        position: relative;

        &.employee {
          .avatar-wrapper {
            border-color: #0096cc;
          }
        }

        &.alumni {
          .avatar-wrapper {
            border-color: #ee7815;
          }
        }

        .avatar-wrapper {
          position: relative;
          border: 1px solid transparent;
          padding: 3px;
          border-radius: 50%;
          cursor: pointer;

          &.is-lock {
            cursor: default;
          }

          .avatar {
            display: block;
            width: 88px;
            height: 88px;
            line-height: 1;
            border-radius: 50%;
          }

          .avatar-lock-icon {
            position: absolute;
            bottom: 18px;
            right: 18px;
            font-size: 34px;
            color: #9e9e9e;
          }
        }

        .badge {
          position: absolute;
          top: 10px;
          left: 0;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #EE7815;
        }

        .avatar-preview-mask {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            overflow: auto;
            margin: 0;
            z-index: 2;
            background: rgba(0, 0, 0, .4);

            .preview-wrapper {
              position: relative;

              .avatar-preview {
                display: block;
                position: absolute;
                top: 150px;
                left: 50%;
                transform: translateX(-50%);
                width: 500px;
                height: 500px;
                line-height: 1;
                border-radius: 50%;
                object-fit: contain;
              }

              .close-preview-icon {
                position: absolute;
                top: 150px;
                left: 50%;
                transform: translateX(-50%);
                font-size: 40px;
                margin-left: 335px;
                cursor: pointer;
              }
            }
          }
      }

      .info-container {
        flex: 1;
        padding-left: 30px;
        overflow: hidden;

        .name-container {
          display: flex;
          align-items: center;
          padding-right: 5px;

          .name {
            min-width: 190px;
            font-size: 24px;
            font-weight: bold;
            line-height: 32px;
            overflow: hidden;
            white-space: nowrap;
            word-break: keep-all;
            text-overflow: ellipsis;
          }

          .gender-icon {
            margin-left: 10px;
            margin-top: 2px;
            font-size: 24px;
          }

          .duplicate-candidate-icon {
            margin-left: 10px;
            margin-top: 4px;
            cursor: pointer;
          }

          .is-copy-application {
            margin-left: 10px;
            margin-top: 4px;
            position: relative;
            line-height: 1;
            display: inline-block;
            overflow: initial;
            cursor: pointer;

            .iconfont {
              font-size: 20px;
              color: rgb(107, 106, 106);
            }

            .dot {
              font-size: 16px;
              transform: scale(0.7);
              color: #ee7815;
              border-radius: 50%;
              background: #fff;
              position: absolute;
              right: -3px;
              bottom: -5px;
              z-index: 1;
            }
          }

          .cv-owner {
            margin-left: auto;
            margin-right: 0;
            padding: 2px 8px;
            border: 1px solid #c0c4cc;
            border-radius: 4px;
          }
        }
        .info-card {
          display: flex;
          .info {
            flex: 1;
            flex-wrap: wrap;
            margin-top: 8px;
            justify-content: space-between;
            .info-item {
              margin-top: 2px;
              width: 33%;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;

              &.applied-job {
                display: flex;
                align-items: center;

                .label {
                  flex-shrink: 0;
                  white-space: pre-wrap;
                }

                .content {
                  display: block;
                  flex: 1;
                  max-width: 200px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
              }

              &.email {
                cursor: default;

                &.active {
                  cursor: pointer;
                  color: #0096cc;

                  &:hover {
                    color: #00aaff;
                  }
                }
              }

              &.age {
                width: 24%;
              }

              &.location {
                width: 24%;
              }
            }
          }
          .description {
            flex: 1;
            word-break: break-word;
            white-space: normal;
            max-height: 100px;
            line-height: 20px;
            margin-top: 10px;
            color: #606266;
            white-space: pre-wrap;
            overflow: auto;
          }
        }
        .info {
          display: flex;
          flex-wrap: wrap;
          margin-top: 10px;
          justify-content: space-between;

          .info-item {
            margin-top: 10px;
            width: 33%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &.email {
              cursor: default;

              &.active {
                cursor: pointer;
                color: #0096cc;

                &:hover {
                  color: #00aaff;
                }
              }
            }

            &.age {
              width: 24%;
            }

            &.location {
              width: 24%;
            }
          }
        }
      }

      .description {
        flex: 1;
        max-height: 76px;
        word-break: break-word;
        white-space: normal;
      }
    }

    .description {
      max-height: 100px;
      line-height: 20px;
      margin-top: 16px;
      color: #606266;
      white-space: pre-wrap;
      overflow: auto;
    }
  }

  .divider {
    width: 1px;
    margin: 5px 55px;
    background: #e7e7e7;
    flex-shrink: 0;
  }

  .candidate-status-container {
    margin: auto 36px auto 0;
  }

  // .candidate-status {
  //   .profile-pagination {
  //     text-align: right;

  //     .last-page, .next-page {
  //       display: inline-block;
  //       vertical-align: middle;
  //       padding: 8px;
  //     }

  //     .pagination-text {
  //       display: inline-block;
  //       vertical-align: middle;
  //       font-size: 18px;
  //       margin: 0 50px;
  //     }
  //   }

  //   .status-container {
  //     margin-top: 30px;
  //     padding-left: 60px;
  //   }
  // }
}
</style>
